import { circle, ellipse, fill, flat, line, P, poly, rect, shine, type IconDef, type Shape } from '../kit';

// Drawing helpers for this set. Coordinates are in the 32 x 32 icon box.

const r2 = (v: number): number => Math.round(v * 100) / 100;
const RAD = Math.PI / 180;

/** (x, y) turned `deg` degrees clockwise about (cx, cy). */
function turnPt(x: number, y: number, deg: number, cx: number, cy: number): [number, number] {
  const c = Math.cos(deg * RAD);
  const s = Math.sin(deg * RAD);
  return [cx + (x - cx) * c - (y - cy) * s, cy + (x - cx) * s + (y - cy) * c];
}

interface Move {
  /** Scale about (cx, cy). */
  s?: number;
  /** Degrees clockwise about (cx, cy). */
  deg?: number;
  cx?: number;
  cy?: number;
  dx?: number;
  dy?: number;
}

/**
 * Path data (M L H V C Q A Z, absolute or relative) scaled and turned about (cx, cy), then
 * shifted by (dx, dy): a part is drawn upright and then tilted or shrunk into place.
 */
function move(d: string, { s = 1, deg = 0, cx = 16, cy = 16, dx = 0, dy = 0 }: Move): string {
  const c = Math.cos(deg * RAD) * s;
  const sn = Math.sin(deg * RAD) * s;
  const pt = (px: number, py: number): string =>
    `${r2(cx + dx + (px - cx) * c - (py - cy) * sn)} ${r2(cy + dy + (px - cx) * sn + (py - cy) * c)}`;
  const tok = d.match(/[A-Za-z]|-?(?:\d+\.?\d*|\.\d+)/g) ?? [];
  const isCmd = (t: string | undefined): boolean => t !== undefined && /[A-Za-z]/.test(t);
  let i = 0;
  const num = (): number => Number(tok[i++]);
  let cmd = '';
  let [x, y, x0, y0] = [0, 0, 0, 0];
  let out = '';
  while (i < tok.length) {
    if (isCmd(tok[i])) cmd = tok[i++] as string;
    const rel = cmd !== cmd.toUpperCase();
    const C = cmd.toUpperCase();
    // the next point, made absolute
    const next = (): [number, number] => {
      const [px, py] = [num(), num()];
      return rel ? [x + px, y + py] : [px, py];
    };
    if (C === 'M' || C === 'L') {
      [x, y] = next();
      if (C === 'M') [x0, y0] = [x, y];
      out += `${C}${pt(x, y)}`;
      cmd = rel ? 'l' : 'L';
    } else if (C === 'H' || C === 'V') {
      const v = num();
      if (C === 'H') x = rel ? x + v : v;
      else y = rel ? y + v : v;
      out += `L${pt(x, y)}`;
    } else if (C === 'C' || C === 'Q') {
      const ps = Array.from({ length: C === 'C' ? 3 : 2 }, next);
      out += `${C}${ps.map(([px, py]) => pt(px, py)).join(' ')}`;
      [x, y] = ps[ps.length - 1] as [number, number];
    } else if (C === 'A') {
      const [rx, ry, rot] = [r2(num() * s), r2(num() * s), r2(num() + deg)];
      const flags = `${num()} ${num()}`;
      [x, y] = next();
      out += `A${rx} ${ry} ${rot} ${flags} ${pt(x, y)}`;
    } else {
      if (C === 'Z') [x, y] = [x0, y0];
      out += C === 'Z' ? 'Z' : '';
      if (C !== 'Z' || !isCmd(tok[i])) i++;
    }
  }
  return out;
}

/** A rounded rectangle centered on (cx, cy), as absolute path data (so `move` can turn it). */
function box(cx: number, cy: number, w: number, h: number, r: number): string {
  const [x0, x1, y0, y1] = [cx - w / 2, cx + w / 2, cy - h / 2, cy + h / 2];
  const a = `A${r} ${r} 0 0 1`;
  const p = (px: number, py: number): string => `${r2(px)} ${r2(py)}`;
  return (
    `M${p(x0 + r, y0)}L${p(x1 - r, y0)}${a} ${p(x1, y0 + r)}L${p(x1, y1 - r)}${a} ${p(x1 - r, y1)}` +
    `L${p(x0 + r, y1)}${a} ${p(x0, y1 - r)}L${p(x0, y0 + r)}${a} ${p(x0 + r, y0)}Z`
  );
}

/** An ellipse as absolute path data (so `move` can turn it). */
const oval = (cx: number, cy: number, rx: number, ry: number): string =>
  `M${r2(cx - rx)} ${r2(cy)}A${rx} ${ry} 0 1 0 ${r2(cx + rx)} ${r2(cy)}A${rx} ${ry} 0 1 0 ${r2(cx - rx)} ${r2(cy)}Z`;

/** A star with `k` points, outer radius `R`, inner radius `r`, the first point straight up. */
function star(cx: number, cy: number, R: number, r: number, k = 5): string {
  const pts: number[] = [];
  for (let i = 0; i < k * 2; i++) {
    const a = (-90 + (i * 180) / k) * RAD;
    pts.push(cx + (i % 2 ? r : R) * Math.cos(a), cy + (i % 2 ? r : R) * Math.sin(a));
  }
  return poly(...pts);
}

/** `k` spokes around (cx, cy) from radius `from` out to `to`, the first one turned `deg` from straight up. */
function spokes(cx: number, cy: number, from: number, to: number, k: number, deg = 0): string {
  let d = '';
  for (let i = 0; i < k; i++) {
    const a = (deg - 90 + (i * 360) / k) * RAD;
    d += `M${r2(cx + from * Math.cos(a))} ${r2(cy + from * Math.sin(a))}`;
    d += `L${r2(cx + to * Math.cos(a))} ${r2(cy + to * Math.sin(a))}`;
  }
  return d;
}

/** `k` dots of radius `r` in a ring of radius `ring` around (cx, cy). */
function dots(cx: number, cy: number, ring: number, r: number, k: number, deg = 0): string {
  let d = '';
  for (let i = 0; i < k; i++) {
    const a = (deg - 90 + (i * 360) / k) * RAD;
    d += circle(cx + ring * Math.cos(a), cy + ring * Math.sin(a), r);
  }
  return d;
}

/** A pointed leaf from (x1, y1) to (x2, y2), `w` wide each side of its middle, bowed by `bend`. */
function leaf(x1: number, y1: number, x2: number, y2: number, w: number, bend = 0): string {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const px = (-(y2 - y1) / len) * w;
  const py = ((x2 - x1) / len) * w;
  const mx = (x1 + x2) / 2 + px * bend;
  const my = (y1 + y2) / 2 + py * bend;
  return (
    `M${r2(x1)} ${r2(y1)}Q${r2(mx + px)} ${r2(my + py)} ${r2(x2)} ${r2(y2)}` +
    `Q${r2(mx - px)} ${r2(my - py)} ${r2(x1)} ${r2(y1)}Z`
  );
}

/** A round flower head: `k` petals around (cx, cy), about `r` to the petal tips. */
function bloom(cx: number, cy: number, r: number, k = 5, deg = 0): string {
  const ri = r * 0.62;
  const pr = r2(ri * Math.sin(Math.PI / k) * 1.08);
  let d = '';
  for (let i = 0; i <= k; i++) {
    const a = (deg - 90 + (i * 360) / k) * RAD;
    const p = `${r2(cx + ri * Math.cos(a))} ${r2(cy + ri * Math.sin(a))}`;
    d += i === 0 ? `M${p}` : `A${pr} ${pr} 0 1 1 ${p}`;
  }
  return `${d}Z`;
}

/**
 * The sliver of circle (cx, cy, r) on its (dx, dy) side, left over when the same circle is
 * moved back by (dx, dy): a rim of light or shade.
 */
function rim(cx: number, cy: number, r: number, dx: number, dy: number): string {
  const d = Math.hypot(dx, dy);
  const [ux, uy] = [dx / d, dy / d];
  const [mx, my] = [cx - dx / 2, cy - dy / 2];
  const h = Math.sqrt(r * r - (d * d) / 4);
  const p1 = `${r2(mx - uy * h)} ${r2(my + ux * h)}`;
  const p2 = `${r2(mx + uy * h)} ${r2(my - ux * h)}`;
  return `M${p1}A${r} ${r} 0 1 0 ${p2}A${r} ${r} 0 0 1 ${p1}Z`;
}

/** A stroke with its own ink edge: a thick ink line with a thinner colored one on top. */
const tube = (d: string, w: number, ink: number, color: string): Shape[] => [line(d, ink), line(d, w, color)];

/** A "$" (the S and its bar) filling the 32 box; `dollarAt` shrinks it into place. */
const DOLLAR =
  'M21.6 10.6C20.6 8.4 18.6 7.4 16 7.4C12.6 7.4 10.4 9.2 10.4 11.8C10.4 14.8 13.4 15.6 16 16.2' +
  'C18.8 16.8 21.8 17.6 21.8 20.6C21.8 23.2 19.4 24.8 16 24.8C13.2 24.8 11 23.8 10 21.6M16 5.6L16 26.4';
const dollarAt = (cx: number, cy: number, s: number, deg = 0): string =>
  move(DOLLAR, { s, deg, dx: cx - 16, dy: cy - 16 });

/** A green bill's face details: a pale medallion with a $ in it. */
const billFace = (cx: number, cy: number, r: number, deg = 0): Shape[] => [
  flat(P.lime, circle(cx, cy, r)),
  line(dollarAt(cx, cy, r * 0.078, deg), 1.3, P.darkGreen),
];

// The card suits, filling the 32 box (shrunk with `move` for the cards fan).
const SPADE =
  'M16 2.6C18.4 6.4 22.6 9.6 25.6 13C27.4 15 28.4 17 28.4 19.2C28.4 22.4 26 24.8 22.8 24.8' +
  'C20.6 24.8 18.8 23.8 17.6 22.2C17.8 25 18.8 27.4 21.2 29.4L10.8 29.4C13.2 27.4 14.2 25 14.4 22.2' +
  'C13.2 23.8 11.4 24.8 9.2 24.8C6 24.8 3.6 22.4 3.6 19.2C3.6 17 4.6 15 6.4 13C9.4 9.6 13.6 6.4 16 2.6Z';
const HEART =
  'M16 28.4C12.8 25.6 3 19.6 3 11.6C3 7.2 6.2 3.8 10.2 3.8C12.8 3.8 14.8 5.2 16 7.4' +
  'C17.2 5.2 19.2 3.8 21.8 3.8C25.8 3.8 29 7.2 29 11.6C29 19.6 19.2 25.6 16 28.4Z';
const DIAMOND =
  'M16 2.4C18.6 6.8 22.2 11.6 26 16C22.2 20.4 18.6 25.2 16 29.6C13.4 25.2 9.8 20.4 6 16C9.8 11.6 13.4 6.8 16 2.4Z';

/** The fire symbol's two flames, shrunk for the campfire. */
const FLAME =
  'M16 29C9.5 29 6 24.6 6 19.6C6 14 10.5 11.4 11.2 6.6C14.4 8.6 15.6 11.4 15.4 14.2' +
  'C17 12.6 17.6 9.4 17 5.6C22.6 8.6 26 14 26 19.8C26 25 22.4 29 16 29Z';
const FLAME_IN =
  'M16 27.6C12.6 27.6 11 25.4 11 23C11 20.2 13.4 18.8 14 16.4C15.6 17.6 16.4 19.2 16.2 20.6' +
  'C17.2 19.8 17.8 18.4 17.6 16.8C20 18.4 21 20.6 21 23C21 25.6 19.2 27.6 16 27.6Z';

/** One die: a red face over a darker edge, white pips at (dx, dy) from its center. */
function die(cx: number, cy: number, size: number, deg: number, pips: number[]): Shape[] {
  const face = box(cx, cy, size, size, 2.5);
  let spots = '';
  for (let i = 0; i + 1 < pips.length; i += 2) {
    spots += circle(...turnPt(cx + (pips[i] as number), cy + (pips[i + 1] as number), deg, cx, cy), 1.3);
  }
  return [
    fill(P.darkRed, move(face, { deg, cx, cy, dy: 1.6 })),
    fill(P.red, move(face, { deg, cx, cy })),
    flat(P.white, spots),
  ];
}

/** A playing card in the fan; the side cards swing out about a point below. */
const CARD = box(16, 15.4, 12.6, 17.8, 1.6);
const fan = (deg: number): Move => ({ deg, cx: 16, cy: 27 });

/** The dart for the target, drawn level (tip at 16,16, tail to the right), then turned into the bullseye. */
const DART: Move = { deg: -45, dx: -2.4, dy: 2.2 };

/** The party popper: a cone from its tip to its open mouth, `t` of the way along, `side` -1..1 across. */
const POP_TIP = [3.8, 27.8] as const;
const POP_MOUTH = [16, 15.6] as const;
const popAt = (t: number, side: number): [number, number] => [
  r2(POP_TIP[0] + t * (POP_MOUTH[0] - POP_TIP[0]) + side * 3.96 * t),
  r2(POP_TIP[1] + t * (POP_MOUTH[1] - POP_TIP[1]) + side * 3.96 * t),
];
const POP_CONE = poly(...POP_TIP, ...popAt(1, -1), ...popAt(1, 1));
const popBand = (t1: number, t2: number): string =>
  poly(...popAt(t1, -1), ...popAt(t2, -1), ...popAt(t2, 1), ...popAt(t1, 1));
/** The popper's open mouth: an ellipse across the end of the cone. */
const POP_LIP = `M${popAt(1, -1).join(' ')}A5.6 2.2 45 1 0 ${popAt(1, 1).join(' ')}A5.6 2.2 45 1 0 ${popAt(1, -1).join(' ')}Z`;

/** The prize wheel: a gold rim with pegs around eight colored wedges. */
function wheel(cy: number, R: number): Shape[] {
  const Ri = R - 2.6;
  const colors = [P.red, P.yellow, P.blue, P.green, P.red, P.yellow, P.blue, P.green];
  return [
    fill(P.gold, circle(16, cy, R)),
    ...colors.map((c, i) => {
      const [a0, a1] = [(-112.5 + i * 45) * RAD, (-67.5 + i * 45) * RAD];
      const p0 = `${r2(16 + Ri * Math.cos(a0))} ${r2(cy + Ri * Math.sin(a0))}`;
      const p1 = `${r2(16 + Ri * Math.cos(a1))} ${r2(cy + Ri * Math.sin(a1))}`;
      return flat(c, `M16 ${cy}L${p0}A${Ri} ${Ri} 0 0 1 ${p1}Z`);
    }),
    line(circle(16, cy, Ri), 1.1),
    flat(P.white, dots(16, cy, (Ri + R - 0.25) / 2, 0.65, 8, 22.5)),
    fill(P.white, circle(16, cy, 2.2), { w: 1.2 }),
  ];
}

/** A porcelain doll centered on x = cx: long hair, a bow, big staring eyes, a frilly dress. */
function doll(cx: number, hair: string, dress: string, bow: string): Shape[] {
  const x = (v: number): number => r2(cx + v);
  return [
    fill(
      dress,
      `M${x(-2.4)} 15H${x(2.4)}C${x(4.4)} 18.4 ${x(5.8)} 22.4 ${x(6.2)} 25.6` +
        `H${x(-6.2)}C${x(-5.8)} 22.4 ${x(-4.4)} 18.4 ${x(-2.4)} 15Z`,
    ),
    fill(
      P.white,
      `M${x(-6.6)} 24.2H${x(6.6)}V26.4A2.2 1.9 0 0 1 ${x(2.2)} 26.4` +
        `A2.2 1.9 0 0 1 ${x(-2.2)} 26.4A2.2 1.9 0 0 1 ${x(-6.6)} 26.4Z`,
      { w: 1.2 },
    ),
    fill(
      hair,
      `M${x(-5.4)} 15.6C${x(-6.2)} 12.4 ${x(-6)} 5.2 ${x(0)} 5.2C${x(6)} 5.2 ${x(6.2)} 12.4 ${x(5.4)} 15.6Z`,
    ),
    fill(P.white, circle(cx, 11, 4.3)),
    flat(
      hair,
      `M${x(-4.3)} 10.6C${x(-3.9)} 8 ${x(-2.2)} 6.7 ${x(0)} 6.7C${x(2.2)} 6.7 ${x(3.9)} 8 ${x(4.3)} 10.6` +
        `C${x(2.8)} 9.6 ${x(1.5)} 9 ${x(0)} 9.4C${x(-1.5)} 9 ${x(-2.8)} 9.6 ${x(-4.3)} 10.6Z`,
    ),
    fill(
      bow,
      move(oval(x(-2.1), 5, 2.1, 1.5), { deg: 20, cx: x(-2.1), cy: 5 }) +
        move(oval(x(2.1), 5, 2.1, 1.5), { deg: -20, cx: x(2.1), cy: 5 }),
      { w: 1.1 },
    ),
    fill(bow, circle(cx, 5.2, 1), { w: 1.1 }),
    flat(P.black, `${circle(x(-1.6), 11.7, 0.9)}${circle(x(1.6), 11.7, 0.9)}`),
    flat(P.blush, `${circle(x(-2.3), 13.2, 0.75)}${circle(x(2.3), 13.2, 0.75)}`, 0.7),
  ];
}

/** The beamed pair of notes: two tilted heads, two stems, the beam. */
const NOTE_PARTS = [
  move(oval(11.2, 24.3, 4.2, 3.2), { deg: -20, cx: 11.2, cy: 24.3 }),
  move(oval(21.2, 21.3, 4.2, 3.2), { deg: -20, cx: 21.2, cy: 21.3 }),
  rect(13.2, 7.6, 2, 15.6),
  rect(23.2, 4.6, 2, 15.6),
  poly(13.2, 7.6, 25.2, 4.6, 25.2, 8.6, 13.2, 11.6),
];

/** A band of the waving flag between y0 and y1 below its top edge; `canton` stops it at x = 15.4. */
function flagBand(y0: number, y1: number, canton = false): string {
  const [a, b] = [6.4 + y0, 6.4 + y1];
  const down = (y: number): string => `C9.2 ${r2(y - 1.8)} 12.4 ${r2(y - 2)} 15.4 ${r2(y - 0.6)}`;
  const back = (y: number): string => `C12.4 ${r2(y - 2)} 9.2 ${r2(y - 1.8)} 6.4 ${r2(y)}`;
  if (canton) return `M6.4 ${a}${down(a)}L15.4 ${r2(b - 0.6)}${back(b)}Z`;
  return (
    `M6.4 ${a}${down(a)}C18.8 ${r2(a + 1)} 23.8 ${r2(a + 1.6)} 28 ${a}` +
    `L28 ${b}C23.8 ${r2(b + 1.6)} 18.8 ${r2(b + 1)} 15.4 ${r2(b - 0.6)}${back(b)}Z`
  );
}

export const play = {
  /** A stack of green dollar bills. */
  cash: [
    fill(P.green, rect(7.8, 7, 21, 12, 1.6)),
    fill(P.green, rect(5.4, 10, 21, 12, 1.6)),
    fill(P.green, rect(3, 13, 21, 12, 1.6)),
    line(rect(5.2, 15.2, 16.6, 7.6, 0.8), 1.2, P.darkGreen),
    ...billFace(13.5, 19, 3.3),
  ],
  /** A green dollar bill with little white wings, flying away. */
  'money-wings': [
    fill(
      P.white,
      'M12.6 16.6C11.8 12 8.6 9 3.8 8.2C3 9.6 3 11.4 4 12.4C2.8 13.4 2.8 15.4 4 16.2C3.4 17.6 4.2 19.4 6 19.6C6.8 20.6 8.2 21 9.6 20.6Z',
    ),
    fill(
      P.white,
      'M19.4 15.4C20 10.4 23.2 6.8 27.8 5.4C28.8 6.8 28.8 8.6 27.8 9.6C29 10.6 29 12.6 27.6 13.4C28.2 14.8 27.4 16.6 25.6 16.8C24.8 17.8 23.6 18.2 22.4 18Z',
    ),
    fill(P.green, move(box(16, 20.4, 17.6, 10.6, 1.5), { deg: -8, cx: 16, cy: 20.4 })),
    line(move(box(16, 20.4, 14, 7, 0.8), { deg: -8, cx: 16, cy: 20.4 }), 1.2, P.darkGreen),
    ...billFace(16, 20.4, 2.9, -8),
  ],
  /** A burlap sack of money, tied at the neck, with a $ on it. */
  moneybag: [
    fill(
      P.tan,
      'M13 12C11.4 10.4 9.4 8 9.6 5.2C11 6.2 12.2 6.2 13 5C13.8 3.8 15 3.2 16 3.2C17 3.2 18.2 3.8 19 5C19.8 6.2 21 6.2 22.4 5.2C22.6 8 20.6 10.4 19 12Z',
    ),
    fill(
      P.tan,
      'M12.8 11.8C8.6 13.8 4.6 18 4.6 22.4C4.6 26.8 8.6 28.8 16 28.8C23.4 28.8 27.4 26.8 27.4 22.4C27.4 18 23.4 13.8 19.2 11.8Z',
    ),
    fill(P.brown, rect(11.6, 10.4, 8.8, 2.8, 1.4)),
    shine(ellipse(9.2, 20, 1.3, 2.6), 0.45),
    line(dollarAt(16, 21.2, 0.5), 2.2, P.darkGreen),
  ],
  /** A gold coin with a $ on it. */
  coin: [
    fill(P.amber, circle(16, 16, 12.4)),
    flat(P.gold, circle(16, 16, 9.8)),
    line(dollarAt(16, 16, 0.5), 2.4, P.amber),
    shine(rim(16, 16, 9, -1.4, -1.4), 0.6),
  ],
  /** A big bold green dollar sign (it floats over people who have a favor for you). */
  dollar: [...tube(DOLLAR, 4.2, 7, P.green), line('M13.6 9C12.6 9.4 12 10.2 11.9 11.2', 1.3, P.lime)],
  /** An ATM: blue header, screen, keypad, card slot, a bill coming out of the cash slot. */
  atm: [
    fill(P.lightGray, rect(5, 2.8, 22, 24.4, 2.2)),
    flat(P.blue, 'M7.2 3.6H24.8A1.4 1.4 0 0 1 26.2 5V7.2H5.8V5A1.4 1.4 0 0 1 7.2 3.6Z'),
    fill(P.sky, rect(8.4, 9, 15.2, 7, 1)),
    line('M10.6 11.6H17.4M10.6 14.2H14.6', 1.4, P.white),
    flat(
      P.gray,
      [8.6, 11.4, 14.2].map((kx) => `${rect(kx, 18, 2.2, 1.6)}${rect(kx, 20.4, 2.2, 1.6)}`).join(''),
    ),
    line('M19.4 19.4H23.4', 1.6, P.charcoal),
    fill(P.charcoal, rect(9, 23, 14, 2, 1), { w: 1.2 }),
    fill(P.green, rect(11, 24, 10, 5.2, 0.4), { w: 1.2 }),
    flat(P.lime, circle(16, 26.8, 1.3)),
  ],
  /** A scratch-off lottery ticket: a gold card, its silver panel scratched off to show 777. */
  scratcher: [
    fill(
      P.gold,
      'M5 6.4H27A2 2 0 0 1 29 8.4V13.6A2.4 2.4 0 0 0 29 18.4V23.6A2 2 0 0 1 27 25.6H5A2 2 0 0 1 3 23.6V18.4A2.4 2.4 0 0 0 3 13.6V8.4A2 2 0 0 1 5 6.4Z',
    ),
    fill(P.silver, rect(6.6, 10.4, 18.8, 11.2, 1.2), { w: 1.2 }),
    flat(P.white, 'M7.2 11H21.4L22.6 12.6L21.4 14.2L22.8 15.8L21.6 17.6L22.6 19.4L21.6 21H7.2Z'),
    line('M8.8 13H12.2L10 19M13.2 13H16.6L14.4 19M17.6 13H21L18.8 19', 2, P.red),
  ],
  /** A slot machine showing 7 7 7, with its lever. */
  slots: [
    fill(P.gold, 'M5 10C5 5.8 8.4 3.4 13.4 3.4C18.4 3.4 21.8 5.8 21.8 10Z'),
    fill(P.gray, rect(23.2, 16, 2.6, 5, 0.8)),
    ...tube('M24.6 17.4L26.6 8.4', 1.8, 3.6, P.silver),
    fill(P.red, circle(26.6, 7.8, 2.2)),
    fill(P.red, rect(3.2, 9.4, 20.4, 19.4, 2.4)),
    fill(P.white, rect(5.6, 12.6, 15.6, 8.6, 1.2)),
    line('M10.8 12.8V21M16 12.8V21', 1.2),
    line('M6.8 14.6H9.8L8 19.2M12 14.6H15L13.2 19.2M17.2 14.6H20.2L18.4 19.2', 1.8, P.red),
    fill(P.charcoal, rect(7, 23.6, 12.8, 2.6, 1), { w: 1.2 }),
    shine(ellipse(10.6, 5.8, 2, 0.9), 0.55),
  ],
  /** A black pool 8-ball. */
  'eight-ball': [
    fill(P.black, circle(16, 16, 12.4)),
    flat(P.charcoal, rim(16, 16, 11.4, 1.8, 1.8)),
    flat(P.white, circle(14.6, 13.8, 5.4)),
    line(`${circle(14.6, 11.9, 1.5)}${circle(14.6, 15.3, 1.9)}`, 1.5, P.black),
    line('M21.6 7.8C23.4 8.8 24.8 10.4 25.4 12.4', 1.6, P.white, 0.5),
  ],
  /** Two red dice. */
  dice: [
    ...die(20.6, 10.8, 12, 15, [-3, -3, 0, 0, 3, 3]),
    ...die(11.8, 18.6, 13.2, -10, [0, 0, -3.3, -3.3, 3.3, -3.3, -3.3, 3.3, 3.3, 3.3]),
  ],
  /** A fan of playing cards, the ace of spades in front. */
  cards: [
    fill(P.white, move(CARD, fan(-20))),
    flat(P.red, move(move(HEART, { s: 0.17, dx: -3.2, dy: -6.2 }), fan(-20))),
    fill(P.white, move(CARD, fan(20))),
    flat(P.red, move(move(DIAMOND, { s: 0.17, dx: 3.6, dy: -6.2 }), fan(20))),
    fill(P.white, CARD),
    flat(P.black, move(SPADE, { s: 0.36, dy: -0.4 })),
  ],
  /** A dartboard target with a dart in the bullseye. */
  target: [
    fill(P.red, circle(13.6, 18.2, 10.4)),
    flat(P.white, circle(13.6, 18.2, 7.4)),
    flat(P.red, circle(13.6, 18.2, 4.6)),
    flat(P.white, circle(13.6, 18.2, 1.9)),
    fill(P.yellow, move(poly(25.6, 15.2, 28.8, 10.4, 31.4, 10.4, 30.8, 15.2), DART), { w: 1.2 }),
    fill(P.yellow, move(poly(25.6, 16.8, 28.8, 21.6, 31.4, 21.6, 30.8, 16.8), DART), { w: 1.2 }),
    fill(P.charcoal, move(box(25.8, 16, 4.4, 1.6, 0.5), DART), { w: 1.2 }),
    fill(P.blue, move(box(20.6, 16, 6.6, 3.6, 1.6), DART)),
  ],
  /** A gold trophy cup. */
  trophy: [
    ...tube('M9.2 7.6C5 7.2 4 10.6 5 12.8C6 14.8 8.4 15.6 11 15.8', 1.6, 4.6, P.gold),
    ...tube('M22.8 7.6C27 7.2 28 10.6 27 12.8C26 14.8 23.6 15.6 21 15.8', 1.6, 4.6, P.gold),
    fill(P.gold, 'M14.2 16.8H17.8L18.6 21.8H13.4Z'),
    fill(P.gold, 'M7.6 3.8H24.4V7.6C24.4 13.8 20.8 17.8 16 17.8C11.2 17.8 7.6 13.8 7.6 7.6Z'),
    fill(P.gold, rect(11, 21.2, 10, 2.8, 1)),
    fill(P.wood, rect(8.6, 23.8, 14.8, 4.6, 1.2)),
    flat(P.gold, rect(12.8, 25.2, 6.4, 1.8, 0.4)),
    shine('M10.4 5.8C10.4 10.4 11.4 13 13 14.8L11.8 15.2C10 13.6 9 11 9.2 5.8Z', 0.55),
  ],
  /** A gold medal on a red and blue ribbon. */
  medal: [
    fill(P.blue, poly(5.6, 3, 12, 3, 19.6, 16.2, 13.6, 18)),
    fill(P.red, poly(26.4, 3, 20, 3, 12.4, 16.2, 18.4, 18)),
    fill(P.gold, circle(16, 21.2, 7.8)),
    line(circle(16, 21.2, 5.4), 1.2, P.amber),
    flat(P.amber, star(16, 21.6, 3.8, 1.6)),
    shine(rim(16, 21.2, 6.8, -1.2, -1.2), 0.55),
  ],
  /** Fireworks: colorful starbursts on a night sky. */
  fireworks: [
    fill(P.navy, rect(3, 3, 26, 26, 4)),
    line(spokes(12.6, 12.6, 2.4, 5.8, 10), 1.6, P.yellow),
    flat(P.yellow, dots(12.6, 12.6, 7.4, 0.9, 10)),
    line(spokes(21.4, 21, 2, 4.6, 8, 22.5), 1.6, P.hotPink),
    flat(P.hotPink, dots(21.4, 21, 6, 0.85, 8, 22.5)),
    line(spokes(22.8, 8.4, 1.2, 3.2, 8, 22.5), 1.4, P.sky),
    flat(P.white, `${circle(7.4, 24.4, 0.9)}${circle(26.4, 14.4, 0.8)}`),
  ],
  /** A party popper: a striped cone, streamers and confetti flying out. */
  party: [
    fill(P.yellow, POP_CONE),
    flat(P.red, popBand(0.3, 0.45)),
    flat(P.blue, popBand(0.62, 0.77)),
    line(POP_CONE, 1.6),
    fill(P.amber, POP_LIP),
    ...tube('M18.6 11.6C17.6 8.8 21.2 8.4 20.4 5.8C20.1 4.8 20.8 4 22 4', 2, 3.6, P.blue),
    ...tube('M21.2 14.2C23.6 11.6 25.8 14.6 27.4 11.6', 2, 3.6, P.hotPink),
    fill(P.yellow, circle(14, 7.2, 1.5), { w: 1.2 }),
    fill(P.lime, circle(25.8, 6.6, 1.4), { w: 1.2 }),
    fill(P.red, circle(25.8, 20.2, 1.5), { w: 1.2 }),
  ],
  /** A carnival prize wheel on a stand: colored wedges, a pointer at the top. */
  'fortune-wheel': [
    fill(P.wood, poly(13.4, 22, 18.6, 22, 21.2, 29, 10.8, 29)),
    ...wheel(15.4, 11),
    fill(P.red, 'M16 8.4L13.4 4.4C13.4 3.5 14.2 3 16 3C17.8 3 18.6 3.5 18.6 4.4Z'),
  ],
  /** A hospital: a white building with a big red cross. */
  hospital: [
    fill(P.white, rect(3.4, 12.2, 25.2, 16.4, 1)),
    fill(P.white, rect(10, 3.6, 12, 25, 1)),
    flat(P.red, 'M14.6 6H17.4V8.8H20.2V11.6H17.4V14.4H14.6V11.6H11.8V8.8H14.6Z'),
    flat(
      P.sky,
      `${rect(5.2, 14.8, 3, 3)}${rect(5.2, 20.6, 3, 3)}${rect(23.8, 14.8, 3, 3)}${rect(23.8, 20.6, 3, 3)}`,
    ),
    fill(P.sky, rect(13.4, 21, 5.2, 7.6, 0.6), { w: 1.2 }),
    line('M16 21.2V28.4', 1.2),
  ],
  /** A camping tent with a little campfire in front. */
  camp: [
    fill(P.darkGreen, poly(11, 4.6, 22.2, 6.4, 27, 23.8, 19.2, 25.4)),
    fill(P.green, poly(3, 25.4, 11, 4.6, 19.2, 25.4)),
    fill(P.charcoal, poly(11, 10.4, 8, 25.4, 14, 25.4), { w: 1.2 }),
    fill(P.wood, move(box(24.8, 25.8, 8, 2.2, 1.1), { deg: -20, cx: 24.8, cy: 25.8 }), { w: 1.2 }),
    fill(P.brown, move(box(24.8, 25.8, 8, 2.2, 1.1), { deg: 20, cx: 24.8, cy: 25.8 }), { w: 1.2 }),
    fill(P.fire, move(FLAME, { s: 0.38, dx: 8.8, dy: 4.8 })),
    flat(P.flame, move(FLAME_IN, { s: 0.34, dx: 8.8, dy: 4.7 })),
  ],
  /** A little desert island with a palm tree. */
  island: [
    fill(P.water, ellipse(16, 24.8, 12.8, 3.8)),
    fill(P.beige, ellipse(15.4, 22.8, 9.2, 3.2)),
    fill(P.wood, 'M13.2 23C13.6 17.8 14.8 12.8 16.6 9L18.6 9.6C17 13.4 16.2 18.2 16.2 23Z'),
    fill(P.green, leaf(17.6, 9.4, 5.2, 13.6, 3.1, -0.55)),
    fill(P.green, leaf(17.6, 9.4, 28.4, 13.6, 3.1, 0.55)),
    fill(P.green, leaf(17.6, 9.4, 7.8, 4.2, 3.1, 0.45)),
    fill(P.green, leaf(17.6, 9.4, 26.8, 4.4, 3.1, -0.45)),
    fill(P.brown, `${circle(16.4, 11.2, 1.5)}${circle(19, 11, 1.5)}`, { w: 1.2 }),
  ],
  /** Two porcelain dolls in frilly dresses (Meemaw's; they're watching). */
  dolls: [...doll(9.4, P.brown, P.pink, P.hotPink), ...doll(22.6, P.gold, P.sky, P.blue)],
  /** A bouquet of mixed flowers in a paper wrap with a bow. */
  flowers: [
    fill(P.green, leaf(16, 17.4, 4.4, 13, 2.2, 0.3)),
    fill(P.green, leaf(16, 17.4, 27.6, 13, 2.2, -0.3)),
    fill(P.purple, bloom(16, 7.2, 4)),
    fill(P.red, bloom(10, 11, 4.6)),
    fill(P.yellow, bloom(22, 10.6, 4.6, 5, 36)),
    fill(P.pink, bloom(16, 14, 4.6, 5, 18)),
    flat(
      P.amber,
      `${circle(10, 11, 1.4)}${circle(22, 10.6, 1.4)}${circle(16, 7.2, 1.2)}${circle(16, 14, 1.4)}`,
    ),
    fill(P.sky, 'M6.6 15.8L11.2 18.6L16 16.2L20.8 18.6L25.4 15.8L17.8 28.8H14.2Z'),
    fill(P.red, 'M16 23.4L12 21V25.8ZM16 23.4L20 21V25.8Z', { w: 1.2 }),
    fill(P.red, circle(16, 23.4, 1.3), { w: 1.2 }),
  ],
  /** A red rose on a leafy stem. */
  rose: [
    ...tube('M16 17L15.4 28', 2, 3.8, P.green),
    fill(P.green, leaf(15.6, 24.4, 8.4, 20, 2.4)),
    fill(P.green, leaf(15.6, 22.4, 23.2, 19.2, 2.4)),
    fill(
      P.darkRed,
      'M9.4 11.6C8.4 7.6 9.8 4.2 12.8 3.8C14.2 3.6 15.4 4.2 16 5.2C16.6 4.2 17.8 3.6 19.2 3.8C22.2 4.2 23.6 7.6 22.6 11.6Z',
    ),
    fill(P.red, 'M12 10.4C11.6 7.6 13.4 6 16 6C18.6 6 20.4 7.6 20 10.4Z'),
    line('M14.4 8.6C14.4 7.6 15.4 7 16.4 7.4C17.4 7.8 17.4 9 16.6 9.6', 1.3, P.darkRed),
    fill(P.red, 'M8.4 9.4C7.8 15.2 11 18.6 16 18.6C14.8 16 14.6 12.8 15.6 10.4C12.8 11.6 10.2 11 8.4 9.4Z'),
    fill(
      P.red,
      'M23.6 9.4C24.2 15.2 21 18.6 16 18.6C13.6 16.6 13 13.6 13.6 11.4C17.4 12 21.4 11.6 23.6 9.4Z',
    ),
  ],
  /** Two music notes, beamed together. */
  notes: [
    // ink under every part first, then the color over all of them, so the joins don't show
    ...NOTE_PARTS.map((d) => fill(P.purple, d, { w: 3.2 })),
    ...NOTE_PARTS.map((d) => flat(P.purple, d)),
  ],
  /** An American flag waving on a pole. */
  'us-flag': [
    ...tube('M5.4 4.4L5.4 27.6', 1.6, 3.4, P.gray),
    fill(P.gold, circle(5.4, 4.4, 1.6), { w: 1.2 }),
    fill(P.white, flagBand(0, 14)),
    ...[0, 4, 8, 12].map((y) => flat(P.red, flagBand(y, y + 2))),
    flat(P.navy, flagBand(0, 8, true)),
    flat(
      P.white,
      [0, 1, 2]
        .map(
          (k) => `${circle(8.8 + k * 2.6, 9.1 - k * 0.6, 0.75)}${circle(8.8 + k * 2.6, 12 - k * 0.6, 0.75)}`,
        )
        .join(''),
    ),
    line(flagBand(0, 14), 1.6),
  ],
  /** Card suit: a spade (solid, no outline, for the corners of playing cards). */
  'suit-spade': [flat(P.black, SPADE)],
  /** Card suit: a heart (solid, no outline). */
  'suit-heart': [flat(P.red, HEART)],
  /** Card suit: a diamond (solid, no outline). */
  'suit-diamond': [flat(P.red, DIAMOND)],
  /** Card suit: a club (solid, no outline). */
  'suit-club': [
    flat(P.black, `${circle(16, 9.2, 5.8)}${circle(9.4, 17.8, 5.8)}${circle(22.6, 17.8, 5.8)}`),
    flat(P.black, poly(16, 12, 11, 17.6, 16, 21, 21, 17.6)),
    flat(P.black, 'M15 17C15 22.6 13.6 26.4 10.4 29.4L21.6 29.4C18.4 26.4 17 22.6 17 17Z'),
  ],
} satisfies Record<string, IconDef>;
